import { diffArrays, diffWords } from 'diff';

import { Rule } from './rules.ts';

// SAME: has the rule already; UPDATE: a version of it is overwritten; ADD: the rule is added, or fills an empty
// default rule; BLOCKED: the channel's own default rule or the client number is in another rule
export type ApplyOutcome = 'SAME' | 'UPDATE' | 'ADD' | 'BLOCKED';

export interface ApplyTarget {
    channelId: string;
    channelName: string;
    outcome: ApplyOutcome;
    config: Rule | null;
    takenClient: string | null;
    isRecommended: boolean;
    leftOutFields: string[];
    leftOutInputs: string[];
    filesToAdd: string[];
    filesToRemove: string[];
    groupKey: string;
}

// what the copy writes into a channel's rule: the rule without what the channel doesn't use; its own "Runs for" and
// Active stay
export const getAppliedRule = (rule: Rule, target: ApplyTarget): Rule => ({
    ...rule,
    fields: rule.fields?.filter((name) => !target.leftOutFields.includes(name)),
    documentInput: rule.documentInput?.filter((name) => !target.leftOutInputs.includes(name)),
});

export const groupByChange = (targets: ApplyTarget[]) => {
    const groups = new Map<string, ApplyTarget[]>();
    targets.forEach((target) => groups.set(target.groupKey, [...(groups.get(target.groupKey) ?? []), target]));
    return [...groups.values()];
};

export interface Segment {
    type: 'same' | 'added' | 'removed';
    text: string;
}

export const getWordSegments = (before: string, after: string): Segment[] =>
    diffWords(before, after).map((change) => ({
        type: change.added ? 'added' : change.removed ? 'removed' : 'same',
        text: change.value,
    }));

export interface Passage {
    type: 'same' | 'added' | 'removed' | 'changed';
    before: string;
    after: string;
}

// the instructions' paragraphs; a removed paragraph followed by an added one counts as changed
export const getPassages = (before: string, after: string): Passage[] => {
    const paragraphs = (text: string) =>
        text
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
    const passages: Passage[] = [];
    let removed: string[] = [];
    let added: string[] = [];
    const flush = () => {
        const paired = Math.min(removed.length, added.length);
        for (let i = 0; i < paired; i++) passages.push({ type: 'changed', before: removed[i], after: added[i] });
        removed.slice(paired).forEach((text) => passages.push({ type: 'removed', before: text, after: '' }));
        added.slice(paired).forEach((text) => passages.push({ type: 'added', before: '', after: text }));
        removed = [];
        added = [];
    };
    const normalize = (text: string) => text.replace(/\s+/g, ' ');
    diffArrays(paragraphs(before), paragraphs(after), {
        comparator: (left, right) => normalize(left) === normalize(right),
    }).forEach((change) => {
        if (change.removed) removed.push(...change.value);
        else if (change.added) added.push(...change.value);
        else {
            flush();
            change.value.forEach((text) => passages.push({ type: 'same', before: text, after: text }));
        }
    });
    flush();
    return passages;
};
